(() => {
  'use strict';

  /* =========================================================
     BHDS Wishlist System v1.2
     Etapa 18C – BHDS karta, druhý obrázek a odstranění všeho
  ========================================================= */

  const ACTIVE_LANGUAGE = String(
    document.documentElement.lang ||
    window.Shopify?.locale ||
    'cs'
  ).toLowerCase().split('-')[0];

  const LOCALES = {
    cs: 'cs-CZ',
    en: 'en-GB',
    de: 'de-DE',
  };

  const TRANSLATIONS = {
    cs: {
      product: 'Produkt',
      noImage: 'Bez obrázku',
      addToWishlist: 'Přidat do oblíbených',
      removeFromWishlist: 'Odebrat z oblíbených',
      addedToWishlist: 'Přidáno do oblíbených',
      removedFromWishlist: 'Odebráno z oblíbených',
      wishlist: 'Moje oblíbené',
      wishlistCountOne: '1 produkt v oblíbených',
      wishlistCountOther: '{count} produktů v oblíbených',
      addToCart: 'Přidat do košíku',
      viewProduct: 'Zobrazit produkt',
      clearAll: 'Odstranit vše',
      clearAllAria: 'Odstranit všechny produkty z oblíbených',
      clearConfirm: 'Opravdu chcete odstranit všechny produkty z oblíbených?',
      clearedAll: 'Všechny produkty byly odebrány z oblíbených',
      summaryZero: 'Zatím nemáte uložené žádné produkty.',
      summaryOne: 'Máte uložený 1 produkt.',
      summaryFew: 'Máte uloženy {count} produkty.',
      summaryOther: 'Máte uloženo {count} produktů.',
      cartCountOne: '1 položka v košíku',
      cartCountOther: '{count} položek v košíku',
      noVariant: 'Produkt nemá dostupnou variantu',
      adding: 'Přidávám…',
      added: 'Přidáno',
      addedToCart: 'Produkt byl přidán do košíku',
      addToCartFailed: 'Produkt se nepodařilo přidat do košíku',
    },
    en: {
      product: 'Product',
      noImage: 'No image',
      addToWishlist: 'Add to favourites',
      removeFromWishlist: 'Remove from favourites',
      addedToWishlist: 'Added to favourites',
      removedFromWishlist: 'Removed from favourites',
      wishlist: 'My favourites',
      wishlistCountOne: '1 product in favourites',
      wishlistCountOther: '{count} products in favourites',
      addToCart: 'Add to cart',
      viewProduct: 'View product',
      clearAll: 'Remove all',
      clearAllAria: 'Remove all products from favourites',
      clearConfirm: 'Do you really want to remove all products from favourites?',
      clearedAll: 'All products have been removed from favourites',
      summaryZero: 'You have no saved products yet.',
      summaryOne: 'You have 1 saved product.',
      summaryFew: 'You have {count} saved products.',
      summaryOther: 'You have {count} saved products.',
      cartCountOne: '1 item in cart',
      cartCountOther: '{count} items in cart',
      noVariant: 'This product has no available variant',
      adding: 'Adding…',
      added: 'Added',
      addedToCart: 'Product added to cart',
      addToCartFailed: 'The product could not be added to the cart',
    },
    de: {
      product: 'Produkt',
      noImage: 'Kein Bild',
      addToWishlist: 'Zu Favoriten hinzufügen',
      removeFromWishlist: 'Aus Favoriten entfernen',
      addedToWishlist: 'Zu Favoriten hinzugefügt',
      removedFromWishlist: 'Aus Favoriten entfernt',
      wishlist: 'Meine Favoriten',
      wishlistCountOne: '1 Produkt in den Favoriten',
      wishlistCountOther: '{count} Produkte in den Favoriten',
      addToCart: 'In den Warenkorb',
      viewProduct: 'Produkt anzeigen',
      clearAll: 'Alle entfernen',
      clearAllAria: 'Alle Produkte aus den Favoriten entfernen',
      clearConfirm: 'Möchten Sie wirklich alle Produkte aus den Favoriten entfernen?',
      clearedAll: 'Alle Produkte wurden aus den Favoriten entfernt',
      summaryZero: 'Sie haben noch keine Produkte gespeichert.',
      summaryOne: 'Sie haben 1 Produkt gespeichert.',
      summaryFew: 'Sie haben {count} Produkte gespeichert.',
      summaryOther: 'Sie haben {count} Produkte gespeichert.',
      cartCountOne: '1 Artikel im Warenkorb',
      cartCountOther: '{count} Artikel im Warenkorb',
      noVariant: 'Für dieses Produkt ist keine Variante verfügbar',
      adding: 'Wird hinzugefügt…',
      added: 'Hinzugefügt',
      addedToCart: 'Produkt wurde in den Warenkorb gelegt',
      addToCartFailed: 'Das Produkt konnte nicht in den Warenkorb gelegt werden',
    },
  };

  const LANGUAGE = TRANSLATIONS[ACTIVE_LANGUAGE]
    ? ACTIVE_LANGUAGE
    : 'cs';

  function t(key, values = {}) {
    let text = TRANSLATIONS[LANGUAGE][key] ?? TRANSLATIONS.cs[key] ?? key;

    Object.entries(values).forEach(([name, value]) => {
      text = text.replaceAll(`{${name}}`, String(value));
    });

    return text;
  }

  const CONFIG = {
    storageKey: 'bhWishlistV1',
    toastDuration: 1800,
    currency: window.Shopify?.currency?.active || 'CZK',
    locale: LOCALES[LANGUAGE] || 'cs-CZ',
  };

  const SELECTORS = {
    button: '[data-bh-wishlist-button]',
    count: '[data-bh-wishlist-count]',
    link: '[data-bh-wishlist-link]',

    page: '[data-bh-wishlist-page]',
    grid: '[data-bh-wishlist-grid]',
    empty: '[data-bh-wishlist-empty]',
    summary: '[data-bh-wishlist-summary]',

    remove: '[data-bh-wishlist-remove]',
    clear: '[data-bh-wishlist-clear]',
    addToCart: '[data-bh-wishlist-add-to-cart]',
    toast: '[data-bh-wishlist-toast]',
  };

  const productRequestCache = new Map();

  /* =========================================================
     Pomocné funkce
  ========================================================= */

  function safeString(value) {
    return String(value ?? '').trim();
  }

  function safeNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function formatMoney(priceInCents) {
    const price = safeNumber(priceInCents) / 100;

    return new Intl.NumberFormat(CONFIG.locale, {
      style: 'currency',
      currency: CONFIG.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  }

  function normalizeUrl(url) {
    const value = safeString(url);

    if (!value) {
      return '#';
    }

    try {
      const parsedUrl = new URL(value, window.location.origin);
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch (error) {
      return value;
    }
  }

  function normalizeImageUrl(image) {
    if (!image) {
      return '';
    }

    if (typeof image === 'string') {
      return image.startsWith('//') ? `https:${image}` : image;
    }

    if (typeof image === 'object') {
      const source =
        image.src ||
        image.url ||
        image.preview_image?.src ||
        '';

      return source.startsWith('//') ? `https:${source}` : source;
    }

    return '';
  }

  function getImageIdentity(image) {
    const normalizedUrl = normalizeImageUrl(image);

    if (!normalizedUrl) {
      return '';
    }

    try {
      const parsedUrl = new URL(
        normalizedUrl,
        window.location.origin
      );

      /*
        Shopify může tentýž obrázek vrátit jednou jako původní URL
        a podruhé s parametry velikosti. Pro porovnání proto používáme
        pouze cestu k souboru bez query parametrů.
      */
      return decodeURIComponent(parsedUrl.pathname)
        .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|x\d+)(?=\.[a-z0-9]+$)/i, '')
        .toLowerCase();
    } catch (error) {
      return normalizedUrl
        .split('?')[0]
        .split('#')[0]
        .toLowerCase();
    }
  }

  function getProductHandle(product) {
    const explicitHandle = safeString(product.handle);

    if (explicitHandle) {
      return explicitHandle;
    }

    const productUrl = safeString(product.url);

    if (!productUrl) {
      return '';
    }

    try {
      const parsedUrl = new URL(productUrl, window.location.origin);
      const match = parsedUrl.pathname.match(/\/products\/([^/?#]+)/i);

      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      const match = productUrl.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    }
  }

  function normalizeProduct(product) {
    return {
      id: safeString(product.id),
      variantId: safeString(product.variantId),
      handle: getProductHandle(product),
      title: safeString(product.title),
      url: normalizeUrl(product.url),
      image: normalizeImageUrl(product.image),
      imageSecondary: normalizeImageUrl(product.imageSecondary),
      price: safeNumber(product.price),
      available:
        typeof product.available === 'boolean'
          ? product.available
          : Boolean(product.variantId),
      addedAt: safeNumber(product.addedAt) || Date.now(),
    };
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);

    if (className) {
      element.className = className;
    }

    if (typeof text === 'string') {
      element.textContent = text;
    }

    return element;
  }

  /* =========================================================
     LocalStorage
  ========================================================= */

  function readWishlist() {
    try {
      const storedValue = localStorage.getItem(CONFIG.storageKey);

      if (!storedValue) {
        return [];
      }

      const parsedValue = JSON.parse(storedValue);

      if (!Array.isArray(parsedValue)) {
        return [];
      }

      return parsedValue
        .map(normalizeProduct)
        .filter((product) => product.id);
    } catch (error) {
      console.warn(
        'BH Wishlist: uložená data nelze přečíst.',
        error
      );

      return [];
    }
  }

  function writeWishlist(items) {
    const normalizedItems = items
      .map(normalizeProduct)
      .filter((product) => product.id);

    localStorage.setItem(
      CONFIG.storageKey,
      JSON.stringify(normalizedItems)
    );

    return normalizedItems;
  }

  function saveWishlist(items, detail = {}) {
    let normalizedItems;

    try {
      normalizedItems = writeWishlist(items);
    } catch (error) {
      console.warn(
        'BH Wishlist: data nelze uložit.',
        error
      );

      return;
    }

    window.dispatchEvent(
      new CustomEvent('bh:wishlist:change', {
        detail: {
          items: normalizedItems,
          count: normalizedItems.length,
          action: detail.action || null,
          product: detail.product || null,
        },
      })
    );
  }

  function saveWishlistSilently(items) {
    try {
      return writeWishlist(items);
    } catch (error) {
      console.warn(
        'BH Wishlist: migrovaná data nelze uložit.',
        error
      );

      return items;
    }
  }

  function containsProduct(items, productId) {
    const normalizedId = safeString(productId);

    return items.some(
      (item) => safeString(item.id) === normalizedId
    );
  }

  function findProductIndex(items, productId) {
    const normalizedId = safeString(productId);

    return items.findIndex(
      (item) => safeString(item.id) === normalizedId
    );
  }

  /* =========================================================
     Data produktu z Wishlist tlačítka
  ========================================================= */

  function getProductFromButton(button) {
    return normalizeProduct({
      id: button.dataset.productId,
      variantId: button.dataset.productVariantId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      imageSecondary: button.dataset.productImageSecondary,
      price: button.dataset.productPrice,
      available: Boolean(button.dataset.productVariantId),
      addedAt: Date.now(),
    });
  }

  /* =========================================================
     Produktová data a migrace starých položek
  ========================================================= */

  async function fetchProductByHandle(handle) {
    const normalizedHandle = safeString(handle);

    if (!normalizedHandle) {
      return null;
    }

    if (productRequestCache.has(normalizedHandle)) {
      return productRequestCache.get(normalizedHandle);
    }

    const productJsonUrl = new URL(
      `${shopRoot()}products/${encodeURIComponent(normalizedHandle)}.js`,
      window.location.origin
    );

    productJsonUrl.searchParams.set('_bh', '18c4');

    const request = fetch(productJsonUrl.toString(), {
      headers: {
        Accept: 'application/json',
      },
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Product request failed: ${response.status}`
          );
        }

        return response.json();
      })
      .catch((error) => {
        console.warn(
          `BH Wishlist: produkt "${normalizedHandle}" nelze načíst.`,
          error
        );

        return null;
      });

    productRequestCache.set(normalizedHandle, request);

    return request;
  }

  function selectPurchasableVariant(productData) {
    if (
      !productData ||
      !Array.isArray(productData.variants) ||
      productData.variants.length === 0
    ) {
      return null;
    }

    return (
      productData.variants.find(
        (variant) => variant.available
      ) || null
    );
  }

  async function enrichWishlistProduct(product) {
    const normalizedProduct = normalizeProduct(product);

    const hasValidSecondaryImage =
      Boolean(normalizedProduct.imageSecondary) &&
      getImageIdentity(normalizedProduct.imageSecondary) !==
        getImageIdentity(normalizedProduct.image);

    if (
      normalizedProduct.variantId &&
      normalizedProduct.available &&
      hasValidSecondaryImage
    ) {
      return normalizedProduct;
    }

    if (!normalizedProduct.handle) {
      return normalizedProduct;
    }

    const productData = await fetchProductByHandle(
      normalizedProduct.handle
    );

    if (!productData) {
      return normalizedProduct;
    }

    const variant = selectPurchasableVariant(productData);

    const featuredImage =
      productData.featured_image ||
      productData.images?.[0] ||
      normalizedProduct.image;

    const primaryImage = normalizeImageUrl(
      normalizedProduct.image || featuredImage
    );

    const primaryImageIdentity = getImageIdentity(primaryImage);

    const productImages = (productData.images || [])
      .map(normalizeImageUrl)
      .filter(Boolean);

    const secondaryImage =
      productImages.find((imageUrl) => {
        return (
          getImageIdentity(imageUrl) !==
          primaryImageIdentity
        );
      }) ||
      productImages[1] ||
      normalizedProduct.imageSecondary ||
      '';

    return normalizeProduct({
      ...normalizedProduct,
      id: normalizedProduct.id || productData.id,
      variantId: variant?.id || '',
      title:
        normalizedProduct.title ||
        productData.title ||
        t('product'),
      url:
        normalizedProduct.url !== '#'
          ? normalizedProduct.url
          : productData.url ||
            `/products/${normalizedProduct.handle}`,
      image: primaryImage,
      imageSecondary: secondaryImage,
      price:
        variant?.price ??
        productData.price ??
        normalizedProduct.price,
      available: Boolean(variant),
    });
  }

  async function migrateWishlistItems(items) {
    const requiresMigration = items.some((product) => {
      const normalizedProduct = normalizeProduct(product);

      const secondaryImageIsMissing =
        !normalizedProduct.imageSecondary;

      const secondaryImageMatchesPrimary =
        Boolean(normalizedProduct.imageSecondary) &&
        getImageIdentity(normalizedProduct.imageSecondary) ===
          getImageIdentity(normalizedProduct.image);

      return (
        !normalizedProduct.variantId ||
        !normalizedProduct.available ||
        secondaryImageIsMissing ||
        secondaryImageMatchesPrimary
      );
    });

    if (!requiresMigration) {
      return items;
    }

    const migratedItems = await Promise.all(
      items.map(enrichWishlistProduct)
    );

    return saveWishlistSilently(migratedItems);
  }

  /* =========================================================
     Wishlist tlačítka na kartách a detailu
  ========================================================= */

  function updateWishlistButton(button, isActive) {
    button.classList.toggle('is-active', isActive);

    button.setAttribute(
      'aria-pressed',
      isActive ? 'true' : 'false'
    );

    const productTitle =
      safeString(button.dataset.productTitle) ||
      t('product');

    const actionText = isActive
      ? t('removeFromWishlist')
      : t('addToWishlist');

    button.setAttribute(
      'aria-label',
      `${actionText}: ${productTitle}`
    );

    button.setAttribute('title', actionText);

    const label = button.querySelector(
      '[data-bh-wishlist-label]'
    );

    if (label) {
      label.textContent = actionText;
    }
  }

  function syncWishlistButtons() {
    const items = readWishlist();

    document
      .querySelectorAll(SELECTORS.button)
      .forEach((button) => {
        updateWishlistButton(
          button,
          containsProduct(
            items,
            button.dataset.productId
          )
        );
      });
  }

  function toggleWishlist(button) {
    const product = getProductFromButton(button);

    if (!product.id) {
      console.warn(
        'BH Wishlist: tlačítku chybí data-product-id.'
      );

      return;
    }

    const items = readWishlist();
    const productIndex = findProductIndex(
      items,
      product.id
    );

    if (productIndex >= 0) {
      const [removedProduct] = items.splice(
        productIndex,
        1
      );

      saveWishlist(items, {
        action: 'remove',
        product: removedProduct,
      });

      showToast(
        t('removedFromWishlist'),
        'remove'
      );

      return;
    }

    items.unshift(product);

    saveWishlist(items, {
      action: 'add',
      product,
    });

    showToast(
      t('addedToWishlist'),
      'add'
    );
  }

  /* =========================================================
     Header – počet oblíbených produktů
  ========================================================= */

  function syncWishlistCount() {
    const count = readWishlist().length;

    document
      .querySelectorAll(SELECTORS.count)
      .forEach((counter) => {
        const visibleCount = counter.querySelector(
          '[aria-hidden="true"]'
        );

        const screenReaderCount =
          counter.querySelector('.visually-hidden');

        const previousCount = Number(
          counter.dataset.previousCount || 0
        );

        if (visibleCount) {
          visibleCount.textContent = String(count);
        }

        if (screenReaderCount) {
          screenReaderCount.textContent =
            count === 1
              ? t('wishlistCountOne')
              : t('wishlistCountOther', { count });
        }

        counter.hidden = count === 0;

        if (
          count > 0 &&
          count !== previousCount
        ) {
          counter.classList.remove('is-updated');

          window.requestAnimationFrame(() => {
            counter.classList.add('is-updated');
          });

          window.setTimeout(() => {
            counter.classList.remove('is-updated');
          }, 400);
        }

        counter.dataset.previousCount =
          String(count);
      });

    document
      .querySelectorAll(SELECTORS.link)
      .forEach((link) => {
        const hasItems = count > 0;

        link.classList.toggle(
          'is-empty',
          !hasItems
        );

        link.classList.toggle(
          'has-items',
          hasItems
        );

        link.setAttribute(
          'aria-label',
          hasItems
            ? `${t('wishlist')}: ${count}`
            : t('wishlist')
        );

        link.setAttribute(
          'title',
          hasItems
            ? `${t('wishlist')} (${count})`
            : t('wishlist')
        );
      });
  }

  /* =========================================================
     Toast
  ========================================================= */

  let toastTimer = null;

  function getToast() {
    let toast = document.querySelector(
      SELECTORS.toast
    );

    if (toast) {
      return toast;
    }

    toast = createElement(
      'div',
      'bh-wishlist-toast'
    );

    toast.setAttribute(
      'data-bh-wishlist-toast',
      ''
    );

    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');

    const icon = createElement(
      'span',
      'bh-wishlist-toast__icon'
    );

    icon.setAttribute('aria-hidden', 'true');

    icon.innerHTML = `
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M12 20.35 10.55 19.03C5.4 14.36 2 11.27 2 7.5 2 4.42 4.42 2 7.5 2c1.74 0 3.41.81 4.5 2.09A6.01 6.01 0 0 1 16.5 2C19.58 2 22 4.42 22 7.5c0 3.77-3.4 6.86-8.55 11.54L12 20.35Z"></path>
      </svg>
    `;

    const text = createElement(
      'span',
      'bh-wishlist-toast__text'
    );

    toast.append(icon, text);
    document.body.appendChild(toast);

    return toast;
  }

  function showToast(message, action = 'add') {
    const toast = getToast();

    const text = toast.querySelector(
      '.bh-wishlist-toast__text'
    );

    if (text) {
      text.textContent = message;
    }

    toast.classList.toggle(
      'is-remove',
      action === 'remove'
    );

    toast.classList.remove('is-visible');

    window.clearTimeout(toastTimer);

    window.requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, CONFIG.toastDuration);
  }

  /* =========================================================
     Wishlist stránka
  ========================================================= */

  function createWishlistCard(product) {
    const card = createElement(
      'article',
      'bh-wishlist-card'
    );

    card.dataset.productId = product.id;

    const mediaLink = createElement(
      'a',
      'bh-wishlist-card__media'
    );

    mediaLink.href = product.url;
    mediaLink.setAttribute(
      'aria-label',
      product.title
    );

    if (product.image) {
      const image = document.createElement('img');

      image.className =
        'bh-wishlist-card__image bh-wishlist-card__image--primary';

      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 533;
      image.height = 533;

      mediaLink.appendChild(image);

      if (product.imageSecondary) {
        const secondaryImage = document.createElement('img');

        secondaryImage.className =
          'bh-wishlist-card__image bh-wishlist-card__image--secondary';

        secondaryImage.src = product.imageSecondary;
        secondaryImage.alt = '';
        secondaryImage.loading = 'lazy';
        secondaryImage.width = 533;
        secondaryImage.height = 533;
        secondaryImage.setAttribute('aria-hidden', 'true');

        mediaLink.classList.add('has-secondary-image');
        mediaLink.appendChild(secondaryImage);
      }
    } else {
      mediaLink.appendChild(
        createElement(
          'div',
          'bh-wishlist-card__placeholder',
          t('noImage')
        )
      );
    }

    const content = createElement(
      'div',
      'bh-wishlist-card__content'
    );

    const title = createElement(
      'h3',
      'bh-wishlist-card__title'
    );

    const titleLink =
      createElement('a');

    titleLink.href = product.url;
    titleLink.textContent =
      product.title || t('product');

    title.appendChild(titleLink);

    const price = createElement(
      'div',
      'bh-wishlist-card__price',
      formatMoney(product.price)
    );

    const actions = createElement(
      'div',
      'bh-wishlist-card__actions'
    );

    if (
      product.variantId &&
      product.available
    ) {
      const cartButton = createElement(
        'button',
        'button button--primary bh-wishlist-card__cart',
        t('addToCart')
      );

      cartButton.type = 'button';

      cartButton.setAttribute(
        'data-bh-wishlist-add-to-cart',
        ''
      );

      cartButton.dataset.variantId =
        product.variantId;

      cartButton.dataset.productId =
        product.id;

      actions.appendChild(cartButton);
    } else {
      const productLink = createElement(
        'a',
        'button button--primary bh-wishlist-card__cart',
        t('viewProduct')
      );

      productLink.href = product.url;
      actions.appendChild(productLink);
    }

    const removeButton = createElement(
      'button',
      'bh-wishlist-card__remove',
      t('removeFromWishlist')
    );

    removeButton.type = 'button';

    removeButton.setAttribute(
      'data-bh-wishlist-remove',
      ''
    );

    removeButton.dataset.productId =
      product.id;

    removeButton.setAttribute(
      'aria-label',
      `${t('removeFromWishlist')}: ${product.title}`
    );

    actions.appendChild(removeButton);

    content.append(title, price, actions);
    card.append(mediaLink, content);

    return card;
  }

  function syncWishlistClearButton(count) {
    const page = document.querySelector(SELECTORS.page);

    if (!page) {
      return;
    }

    const header = page.querySelector(
      '.bh-wishlist-page__header'
    );

    if (!header) {
      return;
    }

    let clearButton = header.querySelector(
      SELECTORS.clear
    );

    if (count <= 0) {
      clearButton?.remove();
      header.classList.remove('has-clear-action');
      return;
    }

    if (!clearButton) {
      clearButton = createElement(
        'button',
        'bh-wishlist-page__clear',
        t('clearAll')
      );

      clearButton.type = 'button';
      clearButton.setAttribute(
        'data-bh-wishlist-clear',
        ''
      );
      clearButton.setAttribute(
        'aria-label',
        t('clearAllAria')
      );

      header.appendChild(clearButton);
    }

    header.classList.add('has-clear-action');
  }

  function clearWishlist() {
    const items = readWishlist();

    if (items.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      t('clearConfirm')
    );

    if (!confirmed) {
      return;
    }

    saveWishlist([], {
      action: 'clear',
    });

    showToast(
      t('clearedAll'),
      'remove'
    );
  }

  function updateWishlistSummary(count) {
    document
      .querySelectorAll(SELECTORS.summary)
      .forEach((summary) => {
        if (count === 0) {
          summary.textContent =
            t('summaryZero');

          return;
        }

        if (count === 1) {
          summary.textContent =
            t('summaryOne');

          return;
        }

        if (count >= 2 && count <= 4) {
          summary.textContent =
            t('summaryFew', { count });

          return;
        }

        summary.textContent =
          t('summaryOther', { count });
      });
  }

  async function renderWishlistPage() {
    const page = document.querySelector(
      SELECTORS.page
    );

    if (!page) {
      return;
    }

    const grid = page.querySelector(
      SELECTORS.grid
    );

    const emptyState = page.querySelector(
      SELECTORS.empty
    );

    if (!grid) {
      console.warn(
        'BH Wishlist: chybí data-bh-wishlist-grid.'
      );

      return;
    }

    grid.setAttribute('aria-busy', 'true');

    let items = readWishlist();

    items = await migrateWishlistItems(items);

    grid.replaceChildren();

    updateWishlistSummary(items.length);
    syncWishlistClearButton(items.length);

    if (emptyState) {
      emptyState.hidden = items.length > 0;
    }

    grid.hidden = items.length === 0;

    items.forEach((product) => {
      grid.appendChild(
        createWishlistCard(product)
      );
    });

    grid.removeAttribute('aria-busy');

    syncWishlistButtons();
    syncWishlistCount();
  }

  function removeWishlistProduct(productId) {
    const items = readWishlist();

    const productIndex = findProductIndex(
      items,
      productId
    );

    if (productIndex < 0) {
      return;
    }

    const [removedProduct] = items.splice(
      productIndex,
      1
    );

    saveWishlist(items, {
      action: 'remove',
      product: removedProduct,
    });

    showToast(
      t('removedFromWishlist'),
      'remove'
    );
  }

  /* =========================================================
     Košík
  ========================================================= */

  function setCartCountBubble(cartLink, itemCount) {
    if (!cartLink) {
      return;
    }

    let bubble = cartLink.querySelector(
      '.cart-count-bubble'
    );

    if (itemCount <= 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = createElement(
        'div',
        'cart-count-bubble'
      );

      bubble.innerHTML = `
        <span aria-hidden="true"></span>
        <span class="visually-hidden"></span>
      `;

      cartLink.appendChild(bubble);
    }

    bubble.hidden = false;
    bubble.removeAttribute('hidden');

    const visibleCount = bubble.querySelector(
      '[aria-hidden="true"]'
    );

    const hiddenCount = bubble.querySelector(
      '.visually-hidden'
    );

    if (visibleCount) {
      visibleCount.textContent =
        itemCount < 100
          ? String(itemCount)
          : '99+';
    }

    if (hiddenCount) {
      hiddenCount.textContent =
        itemCount === 1
          ? t('cartCountOne')
          : t('cartCountOther', { count: itemCount });
    }
  }

  async function updateCartIconFallback() {
    try {
      const response = await fetch(
        `${shopRoot()}cart.js`,
        {
          headers: {
            Accept: 'application/json',
          },
          cache: 'no-store',
        }
      );

      if (!response.ok) {
        throw new Error(
          `Cart request failed: ${response.status}`
        );
      }

      const cart = await response.json();

      document
        .querySelectorAll('#cart-icon-bubble')
        .forEach((cartLink) => {
          setCartCountBubble(
            cartLink,
            cart.item_count
          );
        });

      return cart;
    } catch (error) {
      console.warn(
        'BH Wishlist: počet košíku nelze aktualizovat.',
        error
      );

      return null;
    }
  }

  async function refreshCartIcon() {
    await updateCartIconFallback();

    window.setTimeout(
      updateCartIconFallback,
      150
    );

    window.setTimeout(
      updateCartIconFallback,
      500
    );
  }

  async function addWishlistProductToCart(button) {
    const variantId = safeString(
      button.dataset.variantId
    );

    if (!variantId) {
      showToast(
        t('noVariant'),
        'remove'
      );

      return;
    }

    const originalText = button.textContent;

    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = t('adding');

    try {
      const response = await fetch(
        `${shopRoot()}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(
          result.description ||
          `Cart request failed: ${response.status}`
        );
      }

      button.textContent = t('added');
      button.classList.remove('is-loading');

      showToast(
        t('addedToCart'),
        'add'
      );

      await refreshCartIcon();

      document.dispatchEvent(
        new CustomEvent('bh:cart:change', {
          detail: {
            variantId,
            quantity: 1,
          },
        })
      );

      /*
        Po úspěšném přidání zůstane tlačítko ve stavu
        „Přidáno“ a zůstane deaktivované až do obnovení stránky.
      */
      button.disabled = true;
      button.classList.add('is-added');
    } catch (error) {
      console.error(
        'BH Wishlist: produkt nelze přidat do košíku.',
        error
      );

      showToast(
        t('addToCartFailed'),
        'remove'
      );

      button.textContent = originalText;
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  /* =========================================================
     Synchronizace
  ========================================================= */

  function syncWishlist() {
    syncWishlistButtons();
    syncWishlistCount();
    renderWishlistPage();
  }

  /* =========================================================
     Kliknutí
  ========================================================= */

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target.closest(
      SELECTORS.button
    );

    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();

      toggleWishlist(wishlistButton);
      return;
    }

    const removeButton = event.target.closest(
      SELECTORS.remove
    );

    if (removeButton) {
      event.preventDefault();

      removeWishlistProduct(
        removeButton.dataset.productId
      );

      return;
    }

    const clearButton = event.target.closest(
      SELECTORS.clear
    );

    if (clearButton) {
      event.preventDefault();
      clearWishlist();
      return;
    }

    const cartButton = event.target.closest(
      SELECTORS.addToCart
    );

    if (cartButton) {
      event.preventDefault();

      addWishlistProductToCart(cartButton);
    }
  });

  /* =========================================================
     Události
  ========================================================= */

  document.addEventListener(
    'DOMContentLoaded',
    syncWishlist
  );

  document.addEventListener(
    'shopify:section:load',
    syncWishlist
  );

  document.addEventListener(
    'shopify:section:reorder',
    syncWishlist
  );

  window.addEventListener(
    'bh:wishlist:change',
    syncWishlist
  );

  window.addEventListener(
    'storage',
    (event) => {
      if (
        !event.key ||
        event.key === CONFIG.storageKey
      ) {
        syncWishlist();
      }
    }
  );

  /* =========================================================
     Veřejné API
  ========================================================= */

  window.BHWishlist = {
    getItems() {
      return readWishlist();
    },

    getCount() {
      return readWishlist().length;
    },

    contains(productId) {
      return containsProduct(
        readWishlist(),
        productId
      );
    },

    remove(productId) {
      removeWishlistProduct(productId);
    },

    clear() {
      saveWishlist([], {
        action: 'clear',
      });
    },

    refresh() {
      syncWishlist();
    },
  };

  syncWishlist();
})();